/**
 * AdminSuppliers
 *
 * Tedarikçi-merkezli görünüm. Artık bağımsız `suppliers` tablosundan
 * master veriye dayanır. Her tedarikçi için: ürün sayısı (products.suppliers
 * JSONB eşleştirme), stok değeri, ortalama lead time.
 *
 * Detay modu: bir tedarikçiye tıklayınca o tedarikçinin tüm ürünlerinin
 * listesi + seçip toplu PurchaseOrder oluşturma.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  
  Truck,
  Search,
  Package,
  ShoppingCart,
  AlertTriangle,
  MapPin,
  Clock,
  Phone,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function parseSuppliers(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default function AdminSuppliers() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [detailSupplier, setDetailSupplier] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());

  // Fetch master supplier list
  const { data: masterSuppliers = [], isLoading: suppliersLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => base44.entities.Supplier.list(),
    refetchOnWindowFocus: true,
  });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const isLoading = suppliersLoading || productsLoading;

  // Map products to each master supplier by matching name
  const supplierStats = useMemo(() => {
    return masterSuppliers.map(ms => {
      const matchedProducts = [];
      let totalStockValue = 0;
      const leadTimes = [];

      for (const p of products) {
        const suppliers = parseSuppliers(p.suppliers);
        const match = suppliers.find(s => (s?.name || '').trim().toLowerCase() === ms.name.trim().toLowerCase());
        if (match) {
          const stockVal = (Number(p.stock) || 0) * (Number(match.price) || Number(p.price) || 0);
          totalStockValue += stockVal;
          if (match.delivery_time) leadTimes.push(String(match.delivery_time));
          matchedProducts.push({
            id: p.id,
            name: p.name,
            sku: p.sku || p.artikelnummer,
            stock: p.stock || 0,
            min_stock: p.min_stock || 0,
            melde_bestand: p.melde_bestand || 0,
            hoechst_bestand: p.hoechst_bestand || 100,
            lagerort: p.lagerort,
            supplier_price: match.price,
            supplier_delivery_time: match.delivery_time,
          });
        }
      }

      return {
        ...ms,
        productCount: matchedProducts.length,
        totalStockValue,
        leadTimes: leadTimes.length > 0 ? leadTimes : (ms.delivery_time ? [ms.delivery_time] : []),
        products: matchedProducts,
      };
    }).sort((a, b) => b.productCount - a.productCount);
  }, [masterSuppliers, products]);

  const filteredSuppliers = supplierStats.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.contact_person || '').toLowerCase().includes(search.toLowerCase())
  );

  const createPoMutation = useMutation({
    mutationFn: async ({ supplierName, items }) => {
      const results = [];
      for (const it of items) {
        const po = await base44.entities.PurchaseOrder.create({
          product_id: it.productId,
          supplier_name: supplierName,
          ordered_quantity: it.suggestedQty,
          status: 'bestellt',
          created_date: new Date().toISOString(),
        });
        results.push(po);
      }
      return results;
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries(['purchase_orders']);
      toast.success(`${results.length} Bestellung${results.length === 1 ? '' : 'en'} erstellt.`);
      setSelectedProductIds(new Set());
      setDetailSupplier(null);
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const handleCreatePurchaseOrders = () => {
    if (!detailSupplier || selectedProductIds.size === 0) return;
    const items = detailSupplier.products
      .filter(p => selectedProductIds.has(p.id))
      .map(p => ({
        productId: p.id,
        suggestedQty: Math.max(0, (p.hoechst_bestand || 100) - (p.stock || 0)),
        supplierPrice: p.supplier_price,
      }))
      .filter(it => it.suggestedQty > 0);

    if (items.length === 0) {
      toast.info('Ausgewählte Produkte benötigen keine Nachbestellung.');
      return;
    }

    createPoMutation.mutate({
      supplierName: detailSupplier.name,
      items,
    });
  };

  const toggleProduct = (id) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Truck className="w-5 h-5 text-slate-600" />
                Lieferanten
              </h1>
              <p className="text-sm text-slate-500">
                Produktzuordnung, Lieferzeiten und Nachbestellungen
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24 space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Lieferant suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="text-center py-16 text-slate-400">Lade Lieferanten...</div>
        ) : filteredSuppliers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-slate-400">
              <Truck className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p className="font-medium">Keine Lieferanten gefunden.</p>
              <p className="text-xs mt-1">
                Lieferanten werden im Lager-Bereich (Depo → Lieferanten Tab) angelegt.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filteredSuppliers.map(s => (
              <Card
                key={s.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => { setDetailSupplier(s); setSelectedProductIds(new Set()); }}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-bold text-slate-900 truncate">{s.name}</h3>
                      {s.contact_person && <p className="text-[10px] text-slate-500">{s.contact_person}</p>}
                    </div>
                    <Badge variant="outline" className="bg-slate-50 text-slate-600 shrink-0">
                      {s.productCount} Produkte
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-slate-400 uppercase font-bold">Bestandswert</p>
                      <p className="font-bold text-slate-900">
                        €{s.totalStockValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase font-bold">Lieferzeiten</p>
                      <p className="text-slate-700 truncate">
                        {s.leadTimes.length > 0 ? s.leadTimes[0] : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[10px]">
                    {s.phone && <span className="flex items-center gap-1 text-slate-500"><Phone className="w-3 h-3" />{s.phone}</span>}
                    {s.email && <span className="flex items-center gap-1 text-slate-500"><Mail className="w-3 h-3" />{s.email}</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Sheet open={!!detailSupplier} onOpenChange={(open) => !open && setDetailSupplier(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-slate-600" />
              {detailSupplier?.name}
            </SheetTitle>
          </SheetHeader>

          {detailSupplier && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Produkte</p>
                  <p className="text-lg font-bold">{detailSupplier.productCount}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Wert</p>
                  <p className="text-lg font-bold">
                    €{detailSupplier.totalStockValue.toLocaleString('de-DE', { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Nachbestellen</p>
                  <p className="text-lg font-bold text-amber-600">
                    {detailSupplier.products.filter(p =>
                      (p.stock || 0) <= (p.melde_bestand || p.min_stock || 0)
                    ).length}
                  </p>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Artikel</th>
                      <th className="p-2 text-right">Bestand</th>
                      <th className="p-2 text-right">Min/Meld</th>
                      <th className="p-2 text-right">Vorschlag</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {detailSupplier.products.map(p => {
                      const needsReorder = (p.stock || 0) <= (p.melde_bestand || p.min_stock || 0);
                      const suggested = Math.max(0, (p.hoechst_bestand || 100) - (p.stock || 0));
                      return (
                        <tr key={p.id} className={cn(needsReorder && 'bg-amber-50/50')}>
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedProductIds.has(p.id)}
                              onChange={() => toggleProduct(p.id)}
                              disabled={suggested === 0}
                            />
                          </td>
                          <td className="p-2">
                            <div className="font-medium">{p.name}</div>
                            {p.lagerort && (
                              <div className="text-[10px] text-slate-400 flex items-center gap-1">
                                <MapPin className="w-2.5 h-2.5" />
                                {p.lagerort}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-right font-mono">
                            {needsReorder && <AlertTriangle className="w-3 h-3 text-amber-600 inline mr-1" />}
                            {p.stock}
                          </td>
                          <td className="p-2 text-right text-slate-500 font-mono">
                            {p.min_stock}/{p.melde_bestand}
                          </td>
                          <td className="p-2 text-right font-mono font-bold text-emerald-700">
                            {suggested > 0 ? `+${suggested}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDetailSupplier(null)}
                >
                  Schließen
                </Button>
                <Button
                  disabled={selectedProductIds.size === 0 || createPoMutation.isPending}
                  onClick={handleCreatePurchaseOrders}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {selectedProductIds.size} Produkt{selectedProductIds.size === 1 ? '' : 'e'} bestellen
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
